import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { setCollectionContext, type CollectionCtx } from '@/lib/collectionContext';
import { useInView } from '@/lib/useInView';
import { Heart } from 'lucide-react';
import type { ShopifyProduct } from '@/lib/shopifyApi';
import { prefetchProduct } from '@/lib/shopifyApi';
import { useFavorites } from '@/context/FavoritesContext';
import { useImageMagnifier } from '@/hooks/useImageMagnifier';
import { isAddonProduct, productUrl } from '@workspace/url-source';
import { productImageProps } from '@/lib/responsiveProductImage';
import { ProductLabelOverlay } from '@/components/product/ProductLabels';

const DARK  = '#2A1F14';
const GOLD  = '#b83f74';
const MUTED = '#6b6560';
const CREAM = '#ffffff';

interface BadgeDef { tag: string; label: string; color: string }
const BADGES: BadgeDef[] = [
  { tag: 'bestseller', label: 'Bestseller', color: GOLD },
  { tag: 'polecane', label: 'Polecane', color: '#4b807a' },
  { tag: 'dostawa-dzis', label: 'Dostawa dziś', color: GOLD },
  { tag: 'bilecik', label: 'Darmowy bilecik', color: GOLD },
];

export interface CollectionProductCardProps {
  product: ShopifyProduct;
  price: string;
  index: number;
  collectionCtx?: CollectionCtx;
  /** Center the title + price block under the image (used on homepage bestsellers). */
  centerInfo?: boolean;
  /** Hide overlay badge icons on the image (star, sparkle etc.). */
  hideBadges?: boolean;
  /** Keep a single homepage grid while hiding badges on small screens. */
  hideBadgesOnMobile?: boolean;
  /** Optional destination for catalog cards that lead to a configurator instead of the Shopify product page. */
  destinationHref?: string;
  /** Optional title shown below the image while retaining Shopify data for the card image. */
  displayTitle?: string;
  /** Optional image override, for example a dedicated configurator catalog image. */
  imageOverride?: ShopifyProduct['featuredImage'];
  /** Optional label used by the desktop hover CTA. */
  ctaLabel?: string;
  /** Optional content rendered below the price in the standard card information block. */
  extraInfo?: ReactNode;
  /** Hide product-only actions when the card represents a configurator. */
  hideFavorite?: boolean;
  /** Avoid prefetching the source product when the destination is not its product page. */
  disableProductPrefetch?: boolean;
}

export function CollectionProductCard({
  product,
  price,
  index,
  collectionCtx,
  centerInfo,
  hideBadges,
  hideBadgesOnMobile,
  destinationHref,
  displayTitle,
  imageOverride,
  ctaLabel = 'ZOBACZ WARIANTY',
  extraInfo,
  hideFavorite,
  disableProductPrefetch,
}: CollectionProductCardProps) {
  const [hovered, setHovered] = useState(false);
  // The media query controls touch-specific pricing, while pointer events below
  // decide whether the hover overlay should be activated. This keeps the CTA
  // usable with a mouse in browsers that report coarse primary input.
  const [isTouchDevice] = useState(
    () => typeof window !== 'undefined' && !window.matchMedia('(hover: hover)').matches,
  );
  const [isSmallViewport, setIsSmallViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    if (!hideBadgesOnMobile) return;
    const query = window.matchMedia('(max-width: 767px)');
    const update = () => setIsSmallViewport(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [hideBadgesOnMobile]);
  const [, navigate] = useLocation();
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(product.handle);

  const activeBadges = BADGES.filter(b => !product.labels?.some(label =>
    label.name.toLocaleLowerCase('pl-PL') === b.label.toLocaleLowerCase('pl-PL')
    || (b.tag === 'polecane' && label.name.toLocaleLowerCase('pl-PL') === 'polecany')) &&
    (product.tags ?? []).some(t => t.toLowerCase().replace(/\s/g, '-').includes(b.tag)),
  );
  const labelFields = {
    ...product,
    labels: [
      ...(product.labels ?? []),
      ...(!hideBadges && !(hideBadgesOnMobile && isSmallViewport) ? activeBadges.map(badge => ({
        id: `legacy-${badge.tag}`,
        name: badge.label,
        color: badge.color,
        blocks_purchase: false,
        default_message: null,
      })) : []),
    ],
  };

  const addonOnly = isAddonProduct(product);
  const href = destinationHref ?? (addonOnly ? null : productUrl(product.handle));
  const cardTitle = displayTitle ?? product.title;
  const cardImage = imageOverride === undefined ? product.featuredImage : imageOverride;

  /** Navigate to product, storing source collection so the product page can show the back link */
  function goToProduct(e: React.MouseEvent) {
    e.preventDefault();
    if (!href) return;
    if (collectionCtx) setCollectionContext(collectionCtx, href);
    navigate(href);
  }

  const { ref: inViewRef, inView } = useInView<HTMLElement>('-40px 0px');
  const magnifier = useImageMagnifier();

  return (
    <article
      ref={inViewRef}
      onPointerEnter={event => {
        if (event.pointerType === 'touch') return;
        setHovered(true);
        if (!addonOnly && !disableProductPrefetch) prefetchProduct(product.handle);
      }}
      onPointerLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        opacity: inView ? undefined : 0,
        animationDelay: Math.min(index * 0.06, 0.3) > 0 ? `${Math.min(index * 0.06, 0.3)}s` : undefined,
      }}
      className={`bk-labeled-product${inView ? ' bk-fade-up' : ''}`}
    >
      {/* Keep the image link separate from the label and favourite controls. */}
      <div style={{ position: 'relative' }}>
        {href ? <Link href={href} onClick={goToProduct} style={{ display: 'block', textDecoration: 'none' }}>
          <div
            ref={magnifier.containerRef}
            onMouseEnter={magnifier.onMouseEnter}
            onMouseMove={magnifier.onMouseMove}
            onMouseLeave={magnifier.onMouseLeave}
            style={{
              position: 'relative',
              aspectRatio: '1/1',
              overflow: 'hidden',
              background: CREAM,
            }}>
            {cardImage ? (
              <>
                <img
                  {...productImageProps(cardImage.url, cardImage)}
                  alt={cardImage.altText ?? cardTitle}
                  style={{
                    width: '100%', height: '100%', objectFit: 'contain',
                    opacity: magnifier.isZooming ? 0 : 1,
                    transition: 'opacity 0.15s ease',
                  }}
                  loading="lazy"
                  decoding="async"
                />
                {magnifier.enabled && hovered && (
                  <img
                    ref={magnifier.zoomImgRef}
                    src={cardImage.url}
                    alt=""
                    aria-hidden
                    style={{
                      position: 'absolute', inset: 0,
                      width: '100%', height: '100%', objectFit: 'contain',
                      transformOrigin: '0 0',
                      opacity: magnifier.isZooming ? 1 : 0,
                      transition: 'opacity 0.15s ease',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </>
            ) : (
              <div style={{
                width: '100%', height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: MUTED }}>
                  Brak zdjęcia
                </span>
              </div>
            )}

            {/* Subtle dark gradient at bottom */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%',
              background: 'linear-gradient(to top, rgba(42,31,20,0.45) 0%, transparent 100%)',
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.4s ease',
              pointerEvents: 'none',
            }} />

            {/* Visual CTA; the photo itself is the link. */}
            <div
              style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                padding: '0 0 10px',
                display: 'flex', justifyContent: 'center',
                opacity: hovered ? 1 : 0,
                transform: hovered ? 'translateY(0)' : 'translateY(6px)',
                transition: 'opacity 0.35s ease, transform 0.35s ease',
                pointerEvents: 'none',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  padding: '9px 20px',
                  background: 'rgba(250,247,242,0.18)',
                  backdropFilter: 'blur(6px)',
                  border: '1px solid rgba(250,247,242,0.45)',
                  fontFamily: 'DM Sans, sans-serif', fontSize: 10,
                  letterSpacing: '0.15em', textTransform: 'uppercase',
                  color: '#fff',
                }}
              >
                {ctaLabel}
              </span>
            </div>

          </div>
        </Link> : <div style={{ display: 'block' }}>
          <div
            ref={magnifier.containerRef}
            onMouseEnter={magnifier.onMouseEnter}
            onMouseMove={magnifier.onMouseMove}
            onMouseLeave={magnifier.onMouseLeave}
            style={{ position: 'relative', aspectRatio: '1/1', overflow: 'hidden', background: CREAM }}
          >
            {cardImage ? <img {...productImageProps(cardImage.url, cardImage)} decoding="async" alt={cardImage.altText ?? cardTitle} style={{ width: '100%', height: '100%', objectFit: 'contain' }} loading="lazy" /> : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: MUTED }}>Brak zdjęcia</span>
              </div>
            )}
          </div>
        </div>}
        <ProductLabelOverlay product={labelFields} />
        {href && !hideFavorite && (
          <button
            type="button"
            onClick={e => { e.preventDefault(); e.stopPropagation(); toggleFavorite({ handle: product.handle, title: product.title, image: cardImage?.url ?? null, price }); }}
            data-testid={`button-favorite-${index}`}
            className="card-touch-action"
            style={{
              position: 'absolute', top: 10, right: 10,
              width: 44, height: 44, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: favorited ? 'rgba(210,60,120,0.9)' : 'rgba(255,255,255,0.82)',
              backdropFilter: 'blur(6px)',
              border: 'none', cursor: 'pointer',
              transition: 'background 0.2s, opacity 0.2s',
              opacity: hovered || favorited ? 1 : 0,
            }}
            aria-label={favorited ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
          >
            <Heart size={15} strokeWidth={1.8} style={{ color: favorited ? '#fff' : GOLD, fill: favorited ? '#fff' : 'none', transition: 'all 0.2s' }} />
          </button>
        )}
      </div>

      {/* ── Info + Add to cart ── */}
      <div style={{ position: 'relative' }}>
        {href ? <Link href={href} onClick={goToProduct} style={{ textDecoration: 'none', display: 'block' }}>
          <div style={{
            padding: '14px 4px 12px',
            borderBottom: `1px solid ${hovered ? GOLD : 'transparent'}`,
            transition: 'border-color 0.3s ease',
            textAlign: centerInfo ? 'center' : undefined,
          }}>
            <h3 style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: 20, fontWeight: 500, color: DARK,
              margin: '0 0 5px', lineHeight: 1.25,
              letterSpacing: '-0.01em',
            }}>
              {cardTitle}
            </h3>
            <p style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 16, fontWeight: 500,
              color: isTouchDevice || hovered ? GOLD : MUTED,
              margin: 0,
              transition: 'color 0.3s ease',
              letterSpacing: '0.01em',
            }}>
              {price}
            </p>
            {extraInfo}
          </div>
        </Link> : <div style={{ display: 'block' }}>
          <div style={{ padding: '14px 4px 12px', textAlign: centerInfo ? 'center' : undefined }}>
            <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, fontWeight: 500, color: DARK, margin: '0 0 5px', lineHeight: 1.25, letterSpacing: '-0.01em' }}>{cardTitle}</h3>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 16, fontWeight: 500, color: MUTED, margin: 0, letterSpacing: '0.01em' }}>{price}</p>
            {extraInfo}
          </div>
        </div>}

      </div>

      {/* Touch device overrides */}
      <style>{`
        .bk-labeled-product:focus-within .card-touch-action {
          opacity: 1 !important;
        }
        @media (hover: none) {
          .card-touch-action {
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>
    </article>
  );
}
